import React, { useState, useEffect } from "react";
import {
  Grid,
  Box,
  Modal,
  Typography,
  Paper,
  IconButton,
  Button,
  Fab,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Download as DownloadIcon, InfoOutlined, Close as CloseIcon, AutoAwesome } from "@mui/icons-material";
import GenericTable from "../../components/shared/genericTable/genericTable";
import LineGraph from "../../components/shared/lineGraph/lineGraph";
import AIFinanceInsights from "../../components/shared/aiFinanceInsights/AIFinanceInsights";
import { Tabs } from "../../components/shared/tabs/tabs";
import { callAPI } from "../../functions/api";
import { useUser } from "../../functions/userContext";
import { CostViewSideBar } from "./costView.sidebar";

// Helper function to render text with markdown bold (**text**)
const renderTextWithBold = (text) => {
  if (!text) return null;
  
  // Split by **text** pattern and render with bold
  const parts = text.split(/(\*\*.*?\*\*)/g);
  
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      // Remove ** and render as bold
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
};

const CostsView = () => {
  const [costData, setCostData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [costTrend, setCostTrend] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [totalSavings, setTotalSavings] = useState(0);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [selectedKpiInfo, setSelectedKpiInfo] = useState(null);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [rawCosts, setRawCosts] = useState([]);
  const [loadingRawCosts, setLoadingRawCosts] = useState(false);
  const [rawCostsFetched, setRawCostsFetched] = useState(false);
  const { setShowTabs, showTabs, setCurrentTab } = useUser();
  const [activeTab, setActiveTab] = useState(0);
  const tabTitles = ["Overview", "Raw Costs"];
  
  // AI Analysis modal state
  const [aiAnalysisOpen, setAiAnalysisOpen] = useState(false);
  const [aiAnalysisData, setAiAnalysisData] = useState(null);
  const [aiAnalysisLoading, setAiAnalysisLoading] = useState(false);
  
  // Stub data method - will be replaced with actual API call

  // Fetch raw costs data
  const fetchRawCosts = async () => {
    try {
      setLoadingRawCosts(true);
      const rawCostsData = await callAPI("CloudCosts/awsRawCosts", "GET", null, true);
      setRawCosts(Array.isArray(rawCostsData) ? rawCostsData : []);
      setRawCostsFetched(true);
    } catch (error) {
      console.error("Error fetching raw costs:", error);
      setRawCosts([]);
      setRawCostsFetched(true);
    } finally {
      setLoadingRawCosts(false);
    }
  };

  // Fetch AI Analysis data
  const fetchAIAnalysis = async () => {
    try {
      setAiAnalysisLoading(true);
      const analysisData = await callAPI("cloud-costs/aws/kpi/ai-analysis", "GET", null, true);
      console.log("AI Analysis Data received:", analysisData);
      setAiAnalysisData(analysisData);
    } catch (error) {
      console.error("Error fetching AI analysis:", error);
      setAiAnalysisData(null);
    } finally {
      setAiAnalysisLoading(false);
    }
  };

  const handleOpenAIAnalysis = () => {
    setAiAnalysisOpen(true);
    if (!aiAnalysisData) {
      fetchAIAnalysis();
    }
  };

  const handleCloseAIAnalysis = () => {
    setAiAnalysisOpen(false);
  };

  useEffect(() => {
    if (tabTitles[activeTab]) {
      setCurrentTab(tabTitles[activeTab]);
    }
    return () => setCurrentTab(null);
  }, [activeTab, setCurrentTab]);

  // Fetch raw costs only when Raw Costs tab is active
  useEffect(() => {
    if (activeTab === 1 && !rawCostsFetched) {
      fetchRawCosts();
    }
  }, [activeTab, rawCostsFetched]);

  useEffect(() => {
    setShowTabs(false);
    const fetchAll = async () => {
      try {
        setLoading(true);

        // Get real current/previous month costs from API
        const costResponse = await callAPI("CloudCosts/current", "GET", null, true);
        const costTrendData = await callAPI("CloudCosts/costs", "GET", null, true);
        const recommendation = await callAPI(
          "Insights/computeOptimizerRecommendation",
          "GET",
          null,
          true
        );
        setRecommendations(
          Array.isArray(recommendation) ? recommendation : recommendation ? [recommendation] : []
        );

        // Fetch total savings opportunities
        const savingsTotal = await callAPI("insights/totalSavingOpertunities", "GET", null, true);
        setTotalSavings(savingsTotal || 0);

        setCostTrend(costTrendData);

        // Merge real API data with stub data
        const data = {
          lastMonthTotal: costResponse.lastMonthTotal,
          yearToDateAverage: costResponse.yearToDateAverage,
          currentMonthToDate: costResponse.currentMonthToDate,
          projectedEndOfMonth: costResponse.projectedEndOfMonth,
          expectedToDate: costResponse.expectedToDate,
          percentOfMonthComplete: costResponse.percentOfMonthComplete,
          lastMonthVsAvgChange: costResponse.lastMonthVsAvgChange,
          currentVsExpectedChange: costResponse.currentVsExpectedChange,
          // Advanced forecasting metrics
          projectionLow: costResponse.projectionLow,
          projectionHigh: costResponse.projectionHigh,
          confidence: costResponse.confidence,
          anomalousMonthsExcluded: costResponse.anomalousMonthsExcluded,
          methodWeights: costResponse.methodWeights,
          providerBreakdown: {
            aws: costResponse.lastMonthTotal,
            azure: 0,
            gcp: 0,
          },
        };

        setCostData(data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  // Helper to format change data from API
  const formatChange = (changePercent) => {
    return {
      percentage: Math.abs(changePercent).toFixed(1),
      isPositive: changePercent < 0, // For costs: negative change is good (green)
      direction: changePercent > 0 ? "up" : "down",
    };
  }; // Calculate savings trend (mock data - in real app would compare to previous analysis)
  const calculateSavingsTrend = () => {
    // Mock: assume 15% increase in savings opportunities discovered
    return {
      percentage: "15.2",
      isPositive: true, // More savings opportunities is good
      direction: "up",
    };
  };

  // Calculate savings statistics from recommendations
  const calculateSavingsStats = () => {
    if (!recommendations || recommendations.length === 0) return null;

    const savingsValues = recommendations
      .map((r) => r.recommendations?.[0]?.savingOpportunity?.estimatedMonthlySavingsValue || 0)
      .filter((v) => v > 0);

    if (savingsValues.length === 0) return null;

    return {
      count: savingsValues.length,
      average: savingsValues.reduce((sum, v) => sum + v, 0) / savingsValues.length,
      highest: Math.max(...savingsValues),
    };
  };

  // Calculate AWS spend YTD from costTrend data
  const calculateAwsSpendYtd = () => {
    if (!costTrend?.awsCosts?.lobCosts?.Total) return 0;
    // Filter to only include current year months for Y2D calculation
    const currentYear = new Date().getFullYear();
    return costTrend.awsCosts.lobCosts.Total
      .filter(month => {
        const monthYear = parseInt(month.month.split(' ')[1]);
        return monthYear === currentYear;
      })
      .reduce((sum, month) => sum + month.value, 0);
  };

  // Calculate total spending trend (sum of all providers)
  const calculateTotalSpendingTrend = () => {
    if (!costData) return { percentage: 0, isPositive: true };

    const totalCurrent =
      costData.providerBreakdown.aws +
      costData.providerBreakdown.azure +
      costData.providerBreakdown.gcp;
    // Mock previous total (in real app would come from data)
    const totalPrevious = totalCurrent * 0.92; // Assume 8% increase
    const change = ((totalCurrent - totalPrevious) / totalPrevious) * 100;

    return {
      percentage: Math.abs(change).toFixed(1),
      // isPositive controls COLOR: true = green (good), false = red (bad)
      isPositive: change < 0, // For costs: lower total spending is good (green)
      direction: change > 0 ? "up" : "down",
    };
  };

  // Format API data for display
  const lastMonthVsAverage = costData
    ? formatChange(costData.lastMonthVsAvgChange)
    : { percentage: 0, isPositive: true, direction: "up" };
  const currentVsExpected = costData
    ? formatChange(costData.currentVsExpectedChange)
    : { percentage: 0, isPositive: true, direction: "up" };
  const savingsTrend = calculateSavingsTrend();
  const totalSpendingTrend = calculateTotalSpendingTrend();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDisplayValue = (amount) => {
    // Format as plain number with commas for KPI display
    const absAmount = Math.abs(amount);
    const sign = amount < 0 ? "-" : "";

    if (absAmount >= 1000000) {
      return sign + (absAmount / 1000000).toFixed(1) + "M";
    } else if (absAmount >= 1000) {
      return sign + (absAmount / 1000).toFixed(0) + "K";
    }
    return amount.toLocaleString();
  };

  // Function to download the Excel report
  const downloadReport = async (year, includeNonFinal = false) => {
    setDownloadModalOpen(true);
    try {
      // Add minimum delay to show the loading message
      const [result] = await Promise.all([
        callAPI(
          `CloudCosts/report/${year}?includeNonFinal=${includeNonFinal}`,
          "GET",
          null,
          false,
          true,
          `Cloud_Provider_Costs_${year}.xlsx`
        ),
        new Promise((resolve) => setTimeout(resolve, 1500)), // Minimum 1.5s delay
      ]);
    } catch (error) {
      console.error("Error downloading report:", error);
    } finally {
      setDownloadModalOpen(false);
    }
  };

  if (loading || !costData) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 60px)",
          backgroundColor: "background.default",
        }}
      >
        <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Loading cost data...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ paddingLeft: "5px" }}>
      <Tabs activeTab={activeTab} onTabChange={setActiveTab}>
        <Box
          title="Overview"
          sx={{
            height: "calc(100vh - 60px)",
            marginLeft: "5px",
            backgroundColor: "background.default",
            padding: "0px 24px 0px",
            paddingTop: "20px",
            paddingBottom: "10px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* KPI Cards Row */}
          <Box sx={{ marginBottom: "40px", flexShrink: 0 }}>
            <Grid container spacing={2}>
              {[
                {
                  label: `AWS Services Costs ${new Date(new Date().setMonth(new Date().getMonth() - 1)).toLocaleString("default", { month: "long" })}`,
                  value: costData.lastMonthTotal,
                  change: lastMonthVsAverage,
                  color: "#e91e63",
                  previousValue: costData.yearToDateAverage,
                  changeTooltip: "Compared to year-to-date monthly average",
                  infoTitle: "AWS Services Costs",
                  infoDescription:
                    "This is the raw underlying service cost before any discounts. AWS spending for the previous complete month. The percentage indicator compares this to the year-to-date monthly average, helping identify if spending is trending higher or lower than typical. Green indicates spending below average, while red indicates above-average spending.",
                },
                {
                  label: `Predicted Service Costs ${new Date().toLocaleString("default", { month: "long" })}`,
                  value: costData.currentMonthToDate,
                  change: currentVsExpected,
                  color: "#00bcd4",
                  expectedValue: costData.expectedToDate,
                  projectedValue: costData.projectedEndOfMonth,
                  ytdAverage: costData.yearToDateAverage,
                  progress: costData.percentOfMonthComplete?.toFixed(0),
                  forecastDetails: {
                    confidence: costData.confidence,
                    projectionLow: costData.projectionLow,
                    projectionHigh: costData.projectionHigh,
                    anomalousMonthsExcluded: costData.anomalousMonthsExcluded,
                  },
                  changeTooltip:
                    "Compared to expected cost at this point in the month based on YTD average",
                  infoTitle: "Predicted Service Costs",
                  infoDescription:
                    "This displays current month spending to date versus expected spending at this point in the month. The projection uses advanced forecasting algorithms combining multiple methods (linear regression, seasonal trends, and moving averages) to predict end-of-month costs. Anomalous months are automatically excluded to improve accuracy. The confidence score indicates forecast reliability.",
                },
                {
                  label: "Monthly Savings Opportunities",
                  value: totalSavings,
                  priority:
                    totalSavings > 5000
                      ? "Potential High"
                      : totalSavings > 1000
                        ? "Potential Medium"
                        : "Potential Low",
                  priorityColor:
                    totalSavings > 5000 ? "#f44336" : totalSavings > 1000 ? "#ff9800" : "#4caf50",
                  color: "#9c27b0",
                  changeTooltip: "Potential monthly cost savings from optimization recommendations",
                  infoTitle: "Monthly Savings Opportunities",
                  infoDescription:
                    "This represents the total potential monthly savings from AWS Compute Optimizer recommendations for your top 20 EC2 instances. Each recommendation is analyzed and prioritized based on savings percentage and performance risk. High priority (>$5,000), Medium ($1,000-$5,000), and Low (<$1,000) classifications help you focus on the most impactful optimizations first.",
                  savingsStats: calculateSavingsStats(),
                },
                {
                  label: "AWS Spend YTD",
                  value: calculateAwsSpendYtd(),
                  color: "#ff9800",
                  changeTooltip: "Total AWS spending year-to-date",
                  infoTitle: "AWS Spend Year-to-Date",
                  infoDescription:
                    "This shows your cumulative AWS spending from January through the current month based on invoice amounts - this is the actual price you pay after discounts, MAP credits, and other adjustments are applied. The average displays your typical monthly spend, while Peak and Low show your highest and lowest complete months (excluding the current partial month). This helps identify spending patterns and seasonal variations throughout the year.",
                  ytdStats: costTrend?.awsCosts?.lobCosts?.Total
                    ? (() => {
                        const currentYear = new Date().getFullYear();
                        const currentMonth =
                          new Date().toLocaleString("default", { month: "short" }) +
                          " " +
                          currentYear;
                        // Filter to only current year months for YTD stats
                        const currentYearMonths = costTrend.awsCosts.lobCosts.Total.filter(
                          (m) => parseInt(m.month.split(' ')[1]) === currentYear
                        );
                        const completedMonths = currentYearMonths.filter(
                          (m) => m.month !== currentMonth
                        );
                        return {
                          average:
                            currentYearMonths.reduce((sum, m) => sum + m.value, 0) /
                            currentYearMonths.length,
                          highest: Math.max(
                            ...currentYearMonths.map((m) => m.value)
                          ),
                          highestMonth: currentYearMonths.find(
                            (m) =>
                              m.value ===
                              Math.max(...currentYearMonths.map((x) => x.value))
                          )?.month.split(" ")[0],
                          lowest:
                            completedMonths.length > 0
                              ? Math.min(...completedMonths.map((m) => m.value))
                              : 0,
                          lowestMonth:
                            completedMonths.length > 0
                              ? completedMonths
                                  .find(
                                    (m) =>
                                      m.value === Math.min(...completedMonths.map((x) => x.value))
                                  )
                                  ?.month.split(" ")[0]
                              : null,
                        };
                      })()
                    : null,
                },
              ].map((kpi, index) => (
                <Grid item xs={12} sm={6} md={3} key={index} sx={{ display: "flex" }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      height: "140px",
                      width: "100%",
                      position: "relative",
                      background: `linear-gradient(135deg, ${kpi.color}15 0%, ${kpi.color}05 100%)`,
                      border: `1px solid ${kpi.color}30`,
                      borderRadius: 2,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: `0 4px 20px ${kpi.color}40`,
                        border: `1px solid ${kpi.color}60`,
                      },
                    }}
                  >
                    <Box display="flex" flexDirection="column" gap={1}>
                      {/* Header */}
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={600}
                            sx={{ textTransform: "uppercase" }}
                          >
                            {kpi.label}
                          </Typography>
                        </Box>
                        {kpi.priority ? (
                          <Tooltip title={kpi.changeTooltip || ""} placement="top" arrow>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                backgroundColor: `${kpi.priorityColor}20`,
                                cursor: "help",
                              }}
                            >
                              <Typography
                                variant="caption"
                                fontWeight={700}
                                sx={{ color: kpi.priorityColor }}
                              >
                                {kpi.priority}
                              </Typography>
                            </Box>
                          </Tooltip>
                        ) : kpi.change ? (
                          <Tooltip title={kpi.changeTooltip || ""} placement="top" arrow>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                backgroundColor: kpi.change.isPositive ? "#4caf5020" : "#f4433620",
                                cursor: "help",
                              }}
                            >
                              <Typography
                                variant="caption"
                                fontWeight={700}
                                sx={{ color: kpi.change.isPositive ? "#4caf50" : "#f44336" }}
                              >
                                {kpi.change.direction === "up" ? "↑" : "↓"} {kpi.change.percentage}%
                              </Typography>
                            </Box>
                          </Tooltip>
                        ) : null}
                      </Box>

                      {/* Main Value */}
                      {kpi.expectedValue ? (
                        <Box display="flex" alignItems="baseline" gap={1}>
                          <Typography variant="h4" fontWeight={700} sx={{ color: kpi.color }}>
                            ${formatDisplayValue(kpi.value)}
                          </Typography>
                          <Typography
                            variant="h5"
                            fontWeight={500}
                            sx={{ color: "text.secondary" }}
                          >
                            vs
                          </Typography>
                          <Typography
                            variant="h4"
                            fontWeight={700}
                            sx={{ color: `${kpi.color}80` }}
                          >
                            ${formatDisplayValue(kpi.expectedValue)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary", ml: 0.5 }}>
                            avg at this point
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="h4" fontWeight={700} sx={{ color: kpi.color }}>
                          ${formatDisplayValue(kpi.value)}
                        </Typography>
                      )}

                      {/* Additional Info */}
                      <Box>
                        {kpi.previousValue && (
                          <Typography variant="caption" color="text.secondary">
                            YTD Avg: {formatCurrency(kpi.previousValue)}
                          </Typography>
                        )}
                        {kpi.projectedValue &&
                          kpi.ytdAverage &&
                          (() => {
                            const projectedVsAverage =
                              ((kpi.projectedValue - kpi.ytdAverage) / kpi.ytdAverage) * 100;
                            const dollarDifference = kpi.projectedValue - kpi.ytdAverage;
                            let badgeColor;
                            if (projectedVsAverage <= -5) {
                              badgeColor = "#4caf50"; // Green: 5%+ below average
                            } else if (projectedVsAverage >= 5) {
                              badgeColor = "#f44336"; // Red: 5%+ above average
                            } else {
                              badgeColor = "#ff9800"; // Amber: within 5% of average
                            }

                            // Build tooltip content with forecasting details
                            const tooltipContent = kpi.forecastDetails ? (
                              <Box sx={{ p: 0.5 }}>
                                <Typography variant="caption" display="block" fontWeight={600}>
                                  Forecast Details:
                                </Typography>
                                <Typography variant="caption" display="block">
                                  Confidence: {kpi.forecastDetails.confidence}%
                                </Typography>
                                <Typography variant="caption" display="block">
                                  Range: {formatCurrency(kpi.forecastDetails.projectionLow)} -{" "}
                                  {formatCurrency(kpi.forecastDetails.projectionHigh)}
                                </Typography>
                                {kpi.forecastDetails.anomalousMonthsExcluded > 0 && (
                                  <Typography
                                    variant="caption"
                                    display="block"
                                    sx={{ color: "#ff9800" }}
                                  >
                                    ⚠ {kpi.forecastDetails.anomalousMonthsExcluded} anomalous
                                    month(s) excluded
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              ""
                            );

                            return (
                              <Tooltip title={tooltipContent} placement="bottom" arrow>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  sx={{ mt: 0.5, cursor: tooltipContent ? "help" : "default" }}
                                >
                                  Month End Projection: {formatCurrency(kpi.projectedValue)}{" "}
                                  <Box
                                    component="span"
                                    sx={{
                                      px: 0.5,
                                      py: 0.15,
                                      borderRadius: 0.5,
                                      backgroundColor: `${badgeColor}20`,
                                      fontSize: "0.7rem",
                                      fontWeight: 600,
                                      color: badgeColor,
                                    }}
                                  >
                                    {dollarDifference > 0 ? "+" : ""}$
                                    {formatDisplayValue(dollarDifference)}
                                  </Box>
                                  {kpi.forecastDetails && kpi.forecastDetails.confidence && (
                                    <Box
                                      component="span"
                                      sx={{
                                        ml: 0.75,
                                        px: 0.5,
                                        py: 0.15,
                                        borderRadius: 0.5,
                                        backgroundColor: "#2196f320",
                                        fontSize: "0.65rem",
                                        fontWeight: 500,
                                        color: "#2196f3",
                                      }}
                                    >
                                      {kpi.forecastDetails.confidence}% confidence
                                    </Box>
                                  )}
                                </Typography>
                              </Tooltip>
                            );
                          })()}
                        {kpi.projectedValue && !kpi.ytdAverage && (
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color="text.primary"
                            display="block"
                            sx={{ mt: 0.5 }}
                          >
                            Predicted Month End: {formatCurrency(kpi.projectedValue)}
                          </Typography>
                        )}
                        {kpi.breakdown && (
                          <Box display="flex" gap={1} mt={0.5}>
                            {Object.entries(kpi.breakdown).map(([provider, amount]) => {
                              const total = Object.values(kpi.breakdown).reduce((a, b) => a + b, 0);
                              const percentage = Math.round((amount / total) * 100);
                              return (
                                <Typography
                                  key={provider}
                                  variant="caption"
                                  color="text.secondary}"
                                >
                                  {provider.toUpperCase()}: {percentage}%
                                </Typography>
                              );
                            })}
                          </Box>
                        )}
                        {kpi.ytdStats && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ fontSize: "0.7rem", mt: 0.5 }}
                          >
                            Avg: {formatDisplayValue(kpi.ytdStats.average)} • Peak:{" "}
                            {formatDisplayValue(kpi.ytdStats.highest)} ({kpi.ytdStats.highestMonth})
                            • Low: {formatDisplayValue(kpi.ytdStats.lowest)} (
                            {kpi.ytdStats.lowestMonth})
                          </Typography>
                        )}
                        {kpi.savingsStats && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ fontSize: "0.7rem", mt: 0.5 }}
                          >
                            {kpi.savingsStats.count} Recommendations • Avg: $
                            {formatDisplayValue(kpi.savingsStats.average)} • Max: $
                            {formatDisplayValue(kpi.savingsStats.highest)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    {/* Info Icon - Bottom Right */}
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedKpiInfo(kpi);
                        setInfoDialogOpen(true);
                      }}
                      sx={{
                        position: "absolute",
                        bottom: 8,
                        right: 8,
                        padding: 0.5,
                        "&:hover": {
                          backgroundColor: `${kpi.color}20`,
                        },
                      }}
                    >
                      <InfoOutlined sx={{ fontSize: "1rem", color: "text.secondary" }} />
                    </IconButton>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Charts Row - Fills remaining space */}
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <Grid container spacing={2} sx={{ height: "100%" }}>
              <Grid item xs={12} md={6} sx={{ height: "100%" }}>
                <LineGraph
                  title="AWS Cost Actuals (YTD)"
                  description="Monthly spending trends by Line of Business"
                  data={costTrend?.awsCosts?.lobCosts}
                  defaultProvider="ALL"
                  showAll={true}
                  currency={true}
                  splitX={true}
                  showProjection={true}
                  projectedValue={costData.projectedEndOfMonth}
                  projectionLineKey="TOTAL"
                  showMeanLine={true}
                  onDownload={({ includeNonFinal }) => downloadReport(new Date().getFullYear(), includeNonFinal)}
                  downloadTooltip={`Download ${new Date().getFullYear()} Cost Report (Excel)`}
                  downloadDropdown={true}
                />
              </Grid>

              <Grid item xs={12} md={6} sx={{ height: "100%" }}>
                <AIFinanceInsights
                  recommendations={recommendations}
                  aiAnalysisData={aiAnalysisData}
                  aiAnalysisLoading={aiAnalysisLoading}
                  onFetchAIAnalysis={fetchAIAnalysis}
                />
              </Grid>
            </Grid>
          </Box>

          {/* KPI Info Dialog */}
          <Dialog
            open={infoDialogOpen}
            onClose={() => setInfoDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            {selectedKpiInfo && (
              <>
                <DialogTitle sx={{ pb: 1 }}>
                  <Typography variant="h6" fontWeight={600}>
                    {selectedKpiInfo.infoTitle}
                  </Typography>
                </DialogTitle>
                <DialogContent>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {selectedKpiInfo.infoDescription}
                  </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                  <Button onClick={() => setInfoDialogOpen(false)} variant="contained">
                    Close
                  </Button>
                </DialogActions>
              </>
            )}
          </Dialog>

          {/* Download Progress Modal */}
          <Dialog open={downloadModalOpen} maxWidth="sm" fullWidth>
            <DialogContent sx={{ textAlign: "center", py: 6 }}>
              <Box sx={{ position: "relative", display: "inline-flex", mb: 3 }}>
                <CircularProgress
                  size={80}
                  thickness={4}
                  sx={{
                    color: "#F9CA00",
                    animation: "pulse 1.5s ease-in-out infinite",
                    "@keyframes pulse": {
                      "0%": {
                        opacity: 1,
                        transform: "scale(1)",
                      },
                      "50%": {
                        opacity: 0.7,
                        transform: "scale(1.05)",
                      },
                      "100%": {
                        opacity: 1,
                        transform: "scale(1)",
                      },
                    },
                  }}
                />
              </Box>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 1, color: "#F9CA00" }}>
                Generating Cost Report
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please wait while we prepare your cloud provider cost report...
              </Typography>
            </DialogContent>
          </Dialog>
        </Box>

        <Box
          title="Raw Costs"
          sx={{
            height: "calc(100vh - 60px)",
            backgroundColor: "background.default",
          }}
        >
          <Box
            sx={{
              height: "100%",
              paddingLeft: "20px",
              paddingRight: "20px",
              paddingTop: "40px",
              paddingBottom: "20px",
            }}
          >
            <GenericTable
              data={rawCosts}
              loading={loadingRawCosts}
              title="AWS Monthly Raw Costs"
              headerColor="#7E4553"
              alternateRowColor={false}
              showSearch
              columnNameMapping={{
                accountId: "Account ID",
                year: "Year",
                month: "Month",
                metric: "Metric",
                amount: "Amount",
                currency: "Currency",
                isFinal: "Final",
                createdAt: "Created",
                updatedAt: "Updated",
              }}
              customColumns={[
                {
                  name: "Account ID",
                  id: "accountId",
                  align: "left",
                  render: (row) => {
                    return (
                      <Typography variant="body2" fontWeight={600}>
                        {row.accountId || "—"}
                      </Typography>
                    );
                  },
                  score: (row) => row.accountId || "",
                },
                {
                  name: "Date",
                  id: "date",
                  align: "Center",
                  render: (row) => {
                    return (
                      <Typography variant="body2" fontWeight={600}>
                        {/* format data as May 2020 */}
                        {row.year && row.month
                          ? new Date(row.year, row.month - 1).toLocaleString("default", {
                              month: "short",
                              year: "numeric",
                            })
                          : "—"}
                      </Typography>
                    );
                  },
                  score: (row) => {
                    if (row.year && row.month) {
                      return new Date(row.year, row.month - 1).getTime();
                    }
                    return 0;
                  },
                },
                {
                  name: "Amount",
                  id: "amount",
                  align: "Center",
                  render: (row) => {
                    return (
                      <Typography variant="body2" fontWeight={600}>
                        ${row.amount?.toFixed(2) || "0.00"}
                      </Typography>
                    );
                  },
                  score: (row) => row.amount || 0,
                },
                {
                  name: "State",
                  id: "isFinal",
                  align: "Center",
                  render: (row) => {
                    return (
                      <Box
                        sx={{
                          display: "inline-block",
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          backgroundColor: row.isFinal ? "#4caf5020" : "#ff980020",
                          color: row.isFinal ? "#4caf50" : "#ff9800",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                        }}
                      >
                        {row.isFinal ? "✓ Final" : "Pending"}
                      </Box>
                    );
                  },
                  score: (row) => (row.isFinal ? 1 : 0),
                },
                {
                  name: "Created",
                  id: "createdAt",
                  align: "Center",
                  render: (row) => {
                    if (!row.createdAt) return "—";
                    const date = new Date(row.createdAt);
                    const day = date.getDate();
                    const ordinal =
                      day +
                      (day % 10 === 1 && day !== 11
                        ? "st"
                        : day % 10 === 2 && day !== 12
                          ? "nd"
                          : day % 10 === 3 && day !== 13
                            ? "rd"
                            : "th");
                    const month = date.toLocaleString("default", { month: "long" });
                    const year = date.getFullYear();
                    return (
                      <Typography variant="body2" color="text.secondary">
                        {ordinal} of {month} {year}
                      </Typography>
                    );
                  },
                  score: (row) => (row.createdAt ? new Date(row.createdAt).getTime() : 0),
                },
                {
                  name: "Updated",
                  id: "updatedAt",
                  align: "Center",
                  render: (row) => {
                    if (!row.updatedAt) return "—";
                    const date = new Date(row.updatedAt);
                    const day = date.getDate();
                    const ordinal =
                      day +
                      (day % 10 === 1 && day !== 11
                        ? "st"
                        : day % 10 === 2 && day !== 12
                          ? "nd"
                          : day % 10 === 3 && day !== 13
                            ? "rd"
                            : "th");
                    const month = date.toLocaleString("default", { month: "long" });
                    const year = date.getFullYear();
                    return (
                      <Typography variant="body2" color="text.secondary">
                        {ordinal} of {month} {year}
                      </Typography>
                    );
                  },
                  score: (row) => (row.updatedAt ? new Date(row.updatedAt).getTime() : 0),
                },
              ]}
              onRowClick={() => {}}
            />
          </Box>
        </Box>
      </Tabs>
      <CostViewSideBar />
    </Box>
  );
};

export default CostsView;
